<template>
    <main class="flex-1 relative pb-8 z-0 overflow-y-auto">
        <PageHeader>
            <h1 class="ml-3 text-2xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full">
                <span>
                    <span class="flex flex-row items-center">
                        Domain - {{ domain ? domain.translatable.name[locale] : '' }}
                        <button type="button" @click.prevent="handleEditSettings">
                            <svg-vue icon="setting" class="h-8 w-8 text-custom-300" />
                        </button>
                    </span>
                    <LangSwitcher />
                </span>
                <span>
                    <button v-if="isRoot" @click.prevent="handleCreateProject" class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
                        Create project
                    </button>
                </span>
            </h1>
        </PageHeader>

        <div class="mt-8 lg:max-w-6xl lg:mx-auto">
            <h2 class="ml-3 text-1xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full">Projects</h2>
            <div class="m-4">
                <List
                    :items="projects"
                    :columns="columns"
                    :desc="desc"
                    @editItem="handleEditProject"
                    @deleteItem="handleDeleteProject"
                    @manageItem="handleManageProject"
                    editable
                    deletable
                    manageable
                >
                    <template #actions="actionsProps">
                        <a v-if="actionsProps.item.emerige_id" href="/emerige" class="text-indigo-600 hover:text-indigo-900 px-2" @click.prevent="handleSyncEmerige(actionsProps.item)">Sync. w/ Emerige</a>
                        <a v-if="actionsProps.item.immolead_id" href="/immolead" class="text-indigo-600 hover:text-indigo-900 px-2" @click.prevent="handleSyncEmerige(actionsProps.item)">Sync. w/ Immolead</a>
                        <a v-if="actionsProps.item.adlead_id" href="/adlead" class="text-indigo-600 hover:text-indigo-900 px-2" @click.prevent="handleSyncEmerige(actionsProps.item)">Sync. w/ Adlead</a>
                        <a v-if="actionsProps.item.unlatch_id" href="/unlatch" class="text-indigo-600 hover:text-indigo-900 px-2" @click.prevent="handleSyncEmerige(actionsProps.item)">Sync. w/ Unlatch</a>
                    </template>
                </List>
            </div>

            <h2 class="ml-3 text-1xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full">Pages</h2>
            <div class="m-4">
                <div class="flex flex-col">
                    <div class="-my-2 sm:-mx-6 lg:-mx-6">
                        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <div class="shadow border-b border-gray-200 sm:rounded-lg">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            With alt logo ?
                                        </th>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Display page ?
                                        </th>
                                    </tr>
                                    </thead>
                                    <tr v-for="(page, index) in pages" :class="index%2 ? 'bg-gray-50' : 'bg-white'" :key="page.id ? page.id : index">
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            {{ page.name }}
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            <Switch
                                                v-model="page.withAltLogo"
                                                :name="`withAltLogo-${page.id}`"
                                                :id="`withAltLogo-${page.id}`"
                                                @change="handleUpdate(page)"
                                            />
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            <Switch
                                                v-model="page.is_active"
                                                :name="`is_active-${page.id}`"
                                                :id="`is_active-${page.id}`"
                                                @change="handleUpdate(page)"
                                            />
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import PageHeader from "../../components/partials/page-header";
import Loader from "../../components/partials/loader";
import LangSwitcher from "../../components/partials/lang-switcher";
import List from "../../components/partials/list"
import USER_ROLE from "../../enums/user-role";
import Switch from "../../components/form/switch.vue";

export default {
    data() {
        return {
            desc: false
        }
    },
    beforeMount() {
        this.$store.dispatch('domains/getSingle', {id: this.$route.params.id, includes: ['projects', 'settings', 'files', 'pages']})
    },
    computed: {
        isProjectFetchPending() {
            return this.$store.state.domains.isFetchSinglePending
        },
        errors() {
            return this.$store.state.units.errors;
        },
        locale() {
            return this.$store.state.config.locale;
        },
        domain() {
            return this.$store.state.domains.single;
        },
        pages() {
            return this.domain && this.domain.pages ? this.domain.pages.data : [];
        },
        projects() {
            return this.domain && this.domain.projects ? this.domain.projects.data : [];
        },
        authUser() {
            return this.$store.state.auth.auth_user;
        },
        isRoot() {
            if(this.authUser) {
                return Number(this.authUser.role) === Number(USER_ROLE.values.ROOT.description)
            }

            return false
        },
        columns() {
            return {
                id: {},
                name: {}
            }
        }
    },
    methods: {
        handleEditSettings() {
            this.$store.dispatch('modals/open', 'domain_settings-save')
        },
        handleCreateProject() {
            this.$store.dispatch('projects/resetSingle');
            this.$store.dispatch('modals/open', 'project-save')
        },
        handleManageProject(project) {
            this.$router.push({name: 'project-show', params: {id: project.id}})
        },
        handleEditProject(project) {
            this.$store.dispatch('projects/setSingle', project);
            this.$store.dispatch('modals/open', 'project-save');
        },
        handleDeleteProject(project) {
            this.$store.state.projects.single = project;
            this.$store.dispatch('modals/open', 'project-delete');
        },
        handleSyncEmerige(project) {
            this.$store.dispatch('projects/syncEmerige', {id: project.id});
        },
        handleUpdate(page) {
            this.$store.dispatch('pages/update', {single: page});
        },
    },
    components: {
        PageHeader,
        Loader,
        LangSwitcher,
        List,
        Switch
    }
}
</script>
